/**
 * Formats a structured i18n message value by substituting parameters and
 * applying locale-aware formatting (numbers, dates, plurals, selects).
 *
 * Translations are stored as PUFF-J structured values: a string for plain
 * messages, or a tagged object for plural / select / string-array messages.
 * The formatter walks that structure, picks the right branch for plural/
 * select, and resolves any `{name[,type[,style]]}` placeholders in the leaf
 * strings:
 *
 *   {name}                    – plain substitution (toString)
 *   {name, number}            – locale-aware number formatting
 *   {name, number, integer}   – integer (no decimals)
 *   {name, number, percent}   – percentage
 *   {name, date, ::MMMd}      – locale-aware date with a skeleton pattern
 *   {name, time, ::jm}        – locale-aware time with a skeleton pattern
 *   {name, price}             – currency formatting
 *   #                         – inside a plural branch, the plural's number
 */

export type MessageValue = string | PluralMessage | SelectMessage | StringArrayMessage

export interface PluralMessage {
    param: string
    pluralItems: Record<string, MessageValue>
}

export interface SelectMessage {
    param: string
    selectItems: Record<string, MessageValue>
}

export interface StringArrayMessage {
    stringArrayItems: MessageValue[]
}

export function isPluralMessage(v: unknown): v is PluralMessage {
    return typeof v === 'object' && v !== null && 'pluralItems' in v
}

export function isSelectMessage(v: unknown): v is SelectMessage {
    return typeof v === 'object' && v !== null && 'selectItems' in v
}

export function isStringArrayMessage(v: unknown): v is StringArrayMessage {
    return typeof v === 'object' && v !== null && 'stringArrayItems' in v
}

export function isMessageValue(v: unknown): v is MessageValue {
    return (
        typeof v === 'string' || isPluralMessage(v) || isSelectMessage(v) || isStringArrayMessage(v)
    )
}

/**
 * @param value   The structured message value (from the translation bundle)
 * @param params  A record of named values to substitute into the pattern
 * @param locale  A BCP 47 locale tag (e.g. "en-US", "de-DE") for formatting
 * @returns       The fully resolved, human-readable string
 */
export function formatMessage(
    value: MessageValue,
    params: Record<string, unknown>,
    locale: string
): string {
    return formatValue(value, params, locale, undefined)
}

// ---------------------------------------------------------------------------
// Recursive structured formatter
// ---------------------------------------------------------------------------

function formatValue(
    value: MessageValue,
    params: Record<string, unknown>,
    locale: string,
    pluralValue: number | undefined
): string {
    if (typeof value === 'string') {
        return formatString(value, params, locale, pluralValue)
    }

    if (isPluralMessage(value)) {
        const num = toNumber(params[value.param]) ?? 0
        const branch = selectPluralBranch(value.pluralItems, num, locale)
        if (branch == null) return String(num)
        return formatValue(branch, params, locale, num)
    }

    if (isSelectMessage(value)) {
        const key = String(params[value.param] ?? '')
        const branch =
            key in value.selectItems
                ? value.selectItems[key]
                : 'other' in value.selectItems
                  ? value.selectItems['other']
                  : undefined
        if (branch === undefined) return ''
        return formatValue(branch, params, locale, pluralValue)
    }

    if (isStringArrayMessage(value)) {
        return value.stringArrayItems
            .map((item) => formatValue(item, params, locale, pluralValue))
            .join('\n')
    }

    return ''
}

function selectPluralBranch(
    items: Record<string, MessageValue>,
    num: number,
    locale: string
): MessageValue | undefined {
    const exactKey = `=${num}`
    if (exactKey in items) return items[exactKey]

    try {
        const cat = new Intl.PluralRules(locale).select(num)
        if (cat in items) return items[cat]
    } catch {
        // fall through
    }

    return 'other' in items ? items['other'] : undefined
}

// ---------------------------------------------------------------------------
// String pattern formatter (placeholders only — no plural/select)
// ---------------------------------------------------------------------------

function formatString(
    pattern: string,
    params: Record<string, unknown>,
    locale: string,
    pluralValue: number | undefined
): string {
    let result = ''
    let i = 0

    while (i < pattern.length) {
        const ch = pattern[i]

        // ICU MessageFormat quoting:
        //   '' is always a literal apostrophe
        //   '{ '} '# open a quoted-literal block until the next single '
        //   bare ' (followed by anything else) is a literal apostrophe
        if (ch === "'") {
            if (pattern[i + 1] === "'") {
                result += "'"
                i += 2
                continue
            }
            if (isQuotedLiteralOpener(pattern[i + 1])) {
                const end = pattern.indexOf("'", i + 1)
                if (end === -1) {
                    result += pattern.slice(i + 1)
                    break
                }
                result += pattern.slice(i + 1, end)
                i = end + 1
                continue
            }
            result += "'"
            i++
            continue
        }

        // '#' inside a plural branch is the plural's numeric value
        if (ch === '#' && pluralValue !== undefined) {
            result += String(pluralValue)
            i++
            continue
        }

        if (ch === '{') {
            const { text, endIndex } = parseAndFormatArg(pattern, i, params, locale)
            result += text
            i = endIndex + 1
            continue
        }

        result += ch
        i++
    }

    return result
}

function isQuotedLiteralOpener(next: string | undefined): boolean {
    return next === '{' || next === '}' || next === '#' || next === '|'
}

interface ParseResult {
    text: string
    endIndex: number
}

function parseAndFormatArg(
    pattern: string,
    start: number,
    params: Record<string, unknown>,
    locale: string
): ParseResult {
    const inner = extractBracedBlock(pattern, start)
    const endIndex = start + inner.length + 1

    const parts = splitArgParts(inner)
    const name = parts[0].trim()
    const type = parts[1]?.trim().toLowerCase()

    const value = params[name]

    if (!type) {
        return { text: value == null ? '' : String(value), endIndex }
    }

    switch (type) {
        case 'number':
            return { text: formatNumber(value, parts[2]?.trim(), locale), endIndex }

        case 'date':
        case 'time':
            return { text: formatDateTime(value, parts[2]?.trim(), locale), endIndex }

        case 'price':
            return { text: formatPrice(value, locale), endIndex }

        case 'spellout':
        case 'ordinal':
            return { text: formatPlainNumber(value, locale), endIndex }

        default:
            return { text: value == null ? '' : String(value), endIndex }
    }
}

function extractBracedBlock(pattern: string, start: number): string {
    let depth = 0
    let i = start
    let inQuote = false

    while (i < pattern.length) {
        const ch = pattern[i]

        if (ch === "'" && !inQuote) {
            if (pattern[i + 1] === "'") {
                i += 2
                continue
            }
            if (isQuotedLiteralOpener(pattern[i + 1])) {
                inQuote = true
                i++
                continue
            }
            // Bare apostrophe — treat as a literal character.
            i++
            continue
        }
        if (ch === "'" && inQuote) {
            inQuote = false
            i++
            continue
        }

        if (!inQuote) {
            if (ch === '{') depth++
            if (ch === '}') {
                depth--
                if (depth === 0) {
                    return pattern.slice(start + 1, i)
                }
            }
        }
        i++
    }

    return pattern.slice(start + 1)
}

function splitArgParts(inner: string): string[] {
    const parts: string[] = []
    let depth = 0
    let current = ''
    let inQuote = false

    for (let i = 0; i < inner.length; i++) {
        const ch = inner[i]

        if (ch === "'" && !inQuote) {
            if (inner[i + 1] === "'") {
                current += "''"
                i++
                continue
            }
            if (isQuotedLiteralOpener(inner[i + 1])) {
                inQuote = true
                current += ch
                continue
            }
            // Bare apostrophe — literal.
            current += ch
            continue
        }
        if (ch === "'" && inQuote) {
            inQuote = false
            current += ch
            continue
        }

        if (!inQuote) {
            if (ch === '{') depth++
            if (ch === '}') depth--
            if (ch === ',' && depth === 0) {
                parts.push(current)
                current = ''
                continue
            }
        }

        current += ch
    }

    parts.push(current)
    return parts
}

// ---------------------------------------------------------------------------
// Number formatting
// ---------------------------------------------------------------------------

function formatNumber(value: unknown, style: string | undefined, locale: string): string {
    const num = toNumber(value)
    if (num == null) return String(value ?? '')

    try {
        const opts = numberStyleToOptions(style)
        return new Intl.NumberFormat(locale, opts).format(num)
    } catch {
        return String(num)
    }
}

function numberStyleToOptions(style: string | undefined): Intl.NumberFormatOptions {
    if (!style) return {}
    switch (style.toLowerCase()) {
        case 'integer':
            return { maximumFractionDigits: 0 }
        case 'percent':
            return { style: 'percent' }
        case 'currency':
            return { style: 'currency', currency: 'USD' }
        default:
            return {}
    }
}

function formatPlainNumber(value: unknown, locale: string): string {
    const num = toNumber(value)
    if (num == null) return String(value ?? '')
    try {
        return new Intl.NumberFormat(locale).format(num)
    } catch {
        return String(num)
    }
}

// ---------------------------------------------------------------------------
// Price formatting
// ---------------------------------------------------------------------------

interface PriceValue {
    amount: number
    currency: string
}

function formatPrice(value: unknown, locale: string): string {
    try {
        if (isPriceValue(value)) {
            return new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: value.currency,
            }).format(value.amount)
        }

        const num = toNumber(value)
        if (num != null) {
            return new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: 'USD',
            }).format(num)
        }
    } catch {
        // fall through
    }

    return String(value ?? '')
}

function isPriceValue(v: unknown): v is PriceValue {
    return (
        typeof v === 'object' &&
        v !== null &&
        'amount' in v &&
        'currency' in v &&
        typeof v.amount === 'number' &&
        typeof v.currency === 'string'
    )
}

// ---------------------------------------------------------------------------
// Date / Time formatting (ICU skeleton → Intl.DateTimeFormat options)
// ---------------------------------------------------------------------------

function formatDateTime(value: unknown, style: string | undefined, locale: string): string {
    const date = toDate(value)
    if (!date) return String(value ?? '')

    try {
        const opts = skeletonToDateTimeOptions(style)
        return new Intl.DateTimeFormat(locale, opts).format(date)
    } catch {
        return String(value ?? '')
    }
}

/**
 * Converts a date/time skeleton string (e.g. "::MMMd", "::jm") to browser
 * Intl.DateTimeFormat options.
 *
 * Common skeleton characters:
 *   y = year, M = month, d = day, E = weekday, j = hour (locale-preferred),
 *   m = minute, s = second, z = timezone
 */
function skeletonToDateTimeOptions(style: string | undefined): Intl.DateTimeFormatOptions {
    if (!style) return {}

    const skeleton = style.startsWith('::') ? style.slice(2) : style

    switch (skeleton) {
        case 'short':
            return { dateStyle: 'short' }
        case 'medium':
            return { dateStyle: 'medium' }
        case 'long':
            return { dateStyle: 'long' }
        case 'full':
            return { dateStyle: 'full' }
    }

    const opts: Intl.DateTimeFormatOptions = {}
    let i = 0

    while (i < skeleton.length) {
        const ch = skeleton[i]
        let run = 1
        while (i + run < skeleton.length && skeleton[i + run] === ch) run++

        switch (ch) {
            case 'G':
                opts.era = run <= 3 ? 'short' : run === 4 ? 'long' : 'narrow'
                break
            case 'y':
                opts.year = run === 2 ? '2-digit' : 'numeric'
                break
            case 'M':
            case 'L':
                if (run === 1) opts.month = 'numeric'
                else if (run === 2) opts.month = '2-digit'
                else if (run === 3) opts.month = 'short'
                else if (run === 4) opts.month = 'long'
                else opts.month = 'narrow'
                break
            case 'd':
                opts.day = run === 2 ? '2-digit' : 'numeric'
                break
            case 'E':
            case 'e':
            case 'c':
                if (run <= 3) opts.weekday = 'short'
                else if (run === 4) opts.weekday = 'long'
                else opts.weekday = 'narrow'
                break
            case 'h':
                opts.hour = run === 2 ? '2-digit' : 'numeric'
                opts.hour12 = true
                break
            case 'H':
                opts.hour = run === 2 ? '2-digit' : 'numeric'
                opts.hour12 = false
                break
            case 'j':
                // Locale-preferred hour: omit hour12 so Intl.DateTimeFormat
                // picks 12h or 24h per the locale's default hour cycle.
                opts.hour = run === 2 ? '2-digit' : 'numeric'
                break
            case 'm':
                opts.minute = run === 2 ? '2-digit' : 'numeric'
                break
            case 's':
                opts.second = run === 2 ? '2-digit' : 'numeric'
                break
            case 'a':
                break
            case 'z':
                opts.timeZoneName = run <= 3 ? 'short' : 'long'
                break
            default:
                break
        }

        i += run
    }

    return opts
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toNumber(value: unknown): number | undefined {
    if (typeof value === 'number') return value
    if (typeof value === 'string') {
        const n = Number(value)
        return Number.isNaN(n) ? undefined : n
    }
    return undefined
}

function toDate(value: unknown): Date | undefined {
    if (value instanceof Date) return value
    if (typeof value === 'number') return new Date(value)
    if (typeof value === 'string') {
        const d = new Date(value)
        return Number.isNaN(d.getTime()) ? undefined : d
    }
    return undefined
}
